import React, { useState, useMemo, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';

const ACCEPTED_EXTENSIONS = ['xlsx', 'xls', 'csv'];
const STREAM_HEADER = 'Stream';
const DELIVERY_HEADER = 'Delivery Status';
const STREAM_OPTIONS = ['Software Engineering', 'Network Engineering'];
const DELIVERY_STATUS_OPTIONS = [
  'academic delivery completed',
  'graduated',
  'certificate received',
  'certificate dispatched',
  'Dropped',
  'Transfer to another branch',
  'Transfer to another course'
];

const STATUS_STYLES = {
  pending: 'bg-slate-100 text-slate-800',
  uploading: 'bg-amber-100 text-amber-800',
  success: 'bg-emerald-100 text-emerald-900 font-semibold border border-emerald-200',
  error: 'bg-rose-100 text-rose-700',
  blocked: 'bg-rose-50 text-rose-600',
  skipped: 'bg-slate-50 text-slate-500'
};

const sanitizeText = (value) => (value === undefined || value === null ? '' : String(value).trim());

const cleanPayload = (payload) => {
  const next = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      next[key] = value;
    }
  });
  return next;
};

const normalizeStreamValue = (value) => {
  const text = sanitizeText(value);
  if (!text) return '';
  const match = STREAM_OPTIONS.find((option) => option.toLowerCase() === text.toLowerCase());
  return match || null;
};

const normalizeDeliveryStatus = (value) => {
  const text = sanitizeText(value);
  if (!text) return '';
  const match = DELIVERY_STATUS_OPTIONS.find((option) => option.toLowerCase() === text.toLowerCase());
  return match || null;
};

const buildRow = (record, rowNumber) => {
  const studentId = sanitizeText(record['Student ID']);
  const registrationNumber = sanitizeText(record['Registration Number']);
  const streamInput = record[STREAM_HEADER];
  const streamInputText = sanitizeText(streamInput);
  const resolvedStream = normalizeStreamValue(streamInput);
  const deliveryStatusInput = record[DELIVERY_HEADER];
  const deliveryStatusInputText = sanitizeText(deliveryStatusInput);
  const resolvedDeliveryStatus = normalizeDeliveryStatus(deliveryStatusInput);

  const errors = [];
  const invalidColumns = {};

  if (!studentId) errors.push('Student ID is required');
  if (!registrationNumber) errors.push('Registration Number is required');

  if (streamInputText && !resolvedStream) {
    errors.push('Stream value must match the allowed options');
    invalidColumns[STREAM_HEADER] = `Allowed values: ${STREAM_OPTIONS.join(' / ')}`;
  }

  if (deliveryStatusInputText && !resolvedDeliveryStatus) {
    errors.push('Delivery status must match the allowed options');
    invalidColumns[DELIVERY_HEADER] = `Allowed values: ${DELIVERY_STATUS_OPTIONS.join(' / ')}`;
  }

  const updatePayload = cleanPayload({
    stream: resolvedStream || '',
    deliveryStatus: resolvedDeliveryStatus || ''
  });

  return {
    rowNumber,
    rawRecord: record,
    preview: {
      studentId,
      registrationNumber,
      streamInput: streamInputText,
      deliveryStatusInput: deliveryStatusInputText
    },
    studentId,
    registrationNumber,
    resolvedDeliveryStatus,
    resolvedStream,
    updatePayload,
    errors,
    invalidColumns,
    status: errors.length ? 'blocked' : 'pending',
    message: errors.length ? 'Resolve the validation issues before uploading.' : ''
  };
};

export default function UploadPathwayStreams() {
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileMeta, setFileMeta] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const user = AuthService.getUser();
  const hasAdminAccess = Boolean(
    user?.superAdmin?.superAdminId || user?.superAdmin?.staff?.type === 'admin'
  );

  const readyRows = useMemo(() => rows.filter((row) => row.errors.length === 0), [rows]);
  const blockedRows = useMemo(() => rows.filter((row) => row.errors.length > 0), [rows]);
  const successCount = useMemo(() => rows.filter((row) => row.status === 'success').length, [rows]);
  const failedCount = useMemo(() => rows.filter((row) => row.status === 'error').length, [rows]);

  const patchRow = useCallback((rowNumber, updates) => {
    setRows((prev) => prev.map((row) => (row.rowNumber === rowNumber ? { ...row, ...updates } : row)));
  }, []);

  const resetWorkspace = useCallback(() => {
    setRows([]);
    setFileMeta(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const processFile = useCallback(async (file) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ACCEPTED_EXTENSIONS.includes(extension)) {
      Swal.fire('Unsupported file', 'Please upload a .xlsx, .xls, or .csv file.', 'warning');
      return;
    }

    setParsing(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      if (!workbook.SheetNames?.length) {
        throw new Error('This workbook does not contain any sheets.');
      }

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const matrix = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: false,
        blankrows: false
      });

      if (!matrix.length) {
        throw new Error('The worksheet is empty.');
      }

      const headerRow = (matrix[0] || []).map((cell) => sanitizeText(cell));
      const required = ['Student ID', 'Registration Number', STREAM_HEADER, DELIVERY_HEADER];
      const missingColumns = required.filter((column) => !headerRow.includes(column));
      if (missingColumns.length) {
        throw new Error(`Missing required column(s): ${missingColumns.join(', ')}`);
      }

      const dataRows = matrix.slice(1);
      const parsedRows = [];

      dataRows.forEach((row, index) => {
        const record = {};
        headerRow.forEach((header, columnIndex) => {
          if (!header) return;
          record[header] = row[columnIndex] ?? '';
        });

        const isBlank = required.every((column) => sanitizeText(record[column]) === '');
        if (isBlank) {
          return;
        }

        parsedRows.push(buildRow(record, index + 2));
      });

      if (!parsedRows.length) {
        throw new Error('No data rows were found beneath the header.');
      }

      setRows(parsedRows);
      setFileMeta({
        name: file.name,
        sheet: sheetName,
        total: parsedRows.length,
        importedAt: new Date()
      });

      Swal.fire('File ready', `${parsedRows.length} row(s) parsed successfully.`, 'success');
    } catch (error) {
      console.error('Pathway template parsing failed:', error);
      setRows([]);
      setFileMeta(null);
      Swal.fire('Unable to read file', error.message || 'Please double-check the template.', 'error');
    } finally {
      setParsing(false);
    }
  }, []);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      await processFile(file);
      event.target.value = '';
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleUpload = async () => {
    if (!readyRows.length) {
      Swal.fire('No valid rows', 'Please fix the highlighted issues before uploading.', 'info');
      return;
    }

    setUploading(true);
    let success = 0;
    let failure = 0;

    const snapshot = [...rows];
    for (const row of snapshot) {
      if (row.errors.length > 0) {
        patchRow(row.rowNumber, { status: 'blocked', message: 'Fix validation issues in the sheet.' });
        continue;
      }

      const studentId = sanitizeText(row.studentId);
      const registrationNumber = sanitizeText(row.registrationNumber);

      if (!studentId || !registrationNumber) {
        patchRow(row.rowNumber, { status: 'error', message: 'Student ID and Registration Number are required.' });
        failure += 1;
        continue;
      }

      if (!Object.keys(row.updatePayload).length) {
        patchRow(row.rowNumber, {
          status: 'skipped',
          message: 'Skipped: no updateable values provided.'
        });
        continue;
      }

      patchRow(row.rowNumber, { status: 'uploading', message: 'Locating registration...' });

      let lookup;
      try {
        const { data } = await axiosInstance.get(
          `/admin/students/${encodeURIComponent(studentId)}/registration-status`,
          {
            params: { registrationNumber }
          }
        );
        lookup = data;
      } catch (error) {
        patchRow(row.rowNumber, { status: 'error', message: error.response?.data?.message || 'Failed to check registration' });
        failure += 1;
        continue;
      }

      if (!lookup?.registrationExists || !lookup.registration?.registrationId) {
        patchRow(row.rowNumber, {
          status: 'error',
          message: 'Matching registration not found for this Student ID and Registration Number.'
        });
        failure += 1;
        continue;
      }

      const registrationId = lookup.registration.registrationId;

      patchRow(row.rowNumber, { status: 'uploading', message: 'Updating stream...' });

      try {
        await axiosInstance.put(`/admin/registrations/${registrationId}`, row.updatePayload);
        patchRow(row.rowNumber, { status: 'success', message: 'Stream updated successfully.' });
        success += 1;
      } catch (error) {
        const serverMessage = error.response?.data?.message || error.message || 'Failed to update registration';
        patchRow(row.rowNumber, { status: 'error', message: serverMessage });
        failure += 1;
      }
    }

    setUploading(false);
    Swal.fire({
      icon: failure ? 'warning' : 'success',
      title: 'Upload finished',
      text: `${success} registration${success === 1 ? '' : 's'} updated, ${failure} failed.`,
      confirmButtonColor: '#0f172a'
    });
  };

  const uploadDisabled = uploading || !readyRows.length;

  if (!hasAdminAccess) {
    return (
      <DashboardLayout title="Upload Pathway Stream Template">
        <div className="rounded-3xl border border-rose-100 bg-rose-50 p-10 text-center">
          <h2 className="text-xl font-semibold text-rose-700">Access restricted</h2>
          <p className="mt-2 text-sm text-rose-600">
            Only admin users can upload pathway verification templates. Please contact an administrator if you believe this is a mistake.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Upload Pathway Stream Template">
      <div className="space-y-6">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-fuchsia-900 to-slate-800 p-8 text-white shadow-xl">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-3xl space-y-4">
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-white/60">Bulk update</p>
                <h1 className="mt-2 text-3xl font-semibold">Upload Pathway Verification Template</h1>
                <p className="mt-3 text-sm text-white/80">
                  Download the <strong>Pathway_Stream_Template</strong> to capture the stream and delivery status assigned to each HND in Computing registration.
                  Each row is matched using <strong>Student ID</strong> and <strong>Registration Number</strong>. Only the values {STREAM_OPTIONS.join(' or ')} are accepted for the stream, and {DELIVERY_STATUS_OPTIONS.join(', ')} for delivery status.
                </p>
                <p className="text-xs text-white/70">
                  Student name, batch name, and WhatsApp group are included for reference only and are ignored during upload. Use the dropdowns in Excel to prevent typos.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={resetWorkspace}
                disabled={!rows.length && !fileMeta}
                className="inline-flex items-center justify-center rounded-xl border border-white/20 px-6 py-3 text-sm font-semibold text-white/80 transition hover:text-white disabled:cursor-not-allowed disabled:border-white/10 disabled:text-white/40"
              >
                Reset workspace
              </button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
            <p className="text-xs font-semibold text-emerald-600">Rows ready</p>
            <p className="mt-2 text-3xl font-bold text-emerald-800">{readyRows.length}</p>
          </div>
          <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
            <p className="text-xs font-semibold text-rose-600">Rows with issues</p>
            <p className="mt-2 text-3xl font-bold text-rose-800">{blockedRows.length}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold text-slate-500">Updated</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{successCount}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold text-slate-500">Failed</p>
            <p className="mt-2 text-3xl font-bold text-slate-900">{failedCount}</p>
          </div>
        </div>

        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          className="flex cursor-pointer flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-300 bg-white p-10 text-center transition hover:border-fuchsia-400 hover:bg-fuchsia-50"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleFileChange}
          />
          <svg className="h-16 w-16 text-fuchsia-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="mt-4 text-lg font-semibold text-slate-900">
            {parsing ? 'Analyzing file...' : 'Drop the Excel file or click to browse'}
          </p>
          <p className="mt-2 max-w-xl text-sm text-slate-500">
            Accepts .xlsx, .xls, or .csv files generated from the Pathway_Stream_Template. Formatting, formulas, and colors are ignored during import.
          </p>
          {fileMeta && (
            <div className="mt-4 rounded-full bg-slate-100 px-4 py-1 text-xs text-slate-600">
              Loaded {fileMeta.name} • Sheet: {fileMeta.sheet} • {fileMeta.total} rows
            </div>
          )}
        </div>

        {readyRows.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-700">Bulk update</p>
                <p className="text-xs text-slate-500">Review the rows and start the update when ready.</p>
              </div>
              <button
                onClick={handleUpload}
                disabled={uploadDisabled}
                className="inline-flex items-center gap-2 rounded-xl bg-fuchsia-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-fuchsia-700 disabled:cursor-not-allowed disabled:bg-fuchsia-300"
              >
                {uploading ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Updating...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16v14a2 2 0 01-2 2H6a2 2 0 01-2-2z" />
                    </svg>
                    Start update
                  </>
                )}
              </button>
            </div>

            <div className="max-h-[520px] overflow-auto">
              <table className="min-w-max divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Row</th>
                    <th className="px-4 py-3">Student ID</th>
                    <th className="px-4 py-3">Registration Number</th>
                    <th className="px-4 py-3">{DELIVERY_HEADER}</th>
                    <th className="px-4 py-3">{STREAM_HEADER}</th>
                    <th className="px-4 py-3">Validation</th>
                    <th className="px-4 py-3">Update status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        className="px-4 py-12 text-center text-sm text-slate-500"
                        colSpan={7}
                      >
                        Upload the pathway verification template to preview rows.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const statusStyle = STATUS_STYLES[row.status] || STATUS_STYLES.pending;
                      const hasInvalidStream = Boolean(row.invalidColumns[STREAM_HEADER]);
                      const hasInvalidDelivery = Boolean(row.invalidColumns[DELIVERY_HEADER]);
                      return (
                        <tr key={row.rowNumber} className="align-top">
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">{row.rowNumber}</td>
                          <td className="px-4 py-3 text-slate-700">{row.preview.studentId || '—'}</td>
                          <td className="px-4 py-3 text-slate-700">{row.preview.registrationNumber || '—'}</td>
                          <td className="px-4 py-3 text-slate-700">
                            {hasInvalidDelivery ? (
                              <>
                                <span className="font-semibold text-amber-800">{row.preview.deliveryStatusInput || '—'}</span>
                                <p className="mt-1 text-xs text-amber-700">{row.invalidColumns[DELIVERY_HEADER]}</p>
                              </>
                            ) : (
                              row.preview.deliveryStatusInput || '—'
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {hasInvalidStream ? (
                              <>
                                <span className="font-semibold text-amber-800">{row.preview.streamInput || '—'}</span>
                                <p className="mt-1 text-xs text-amber-700">{row.invalidColumns[STREAM_HEADER]}</p>
                              </>
                            ) : (
                              row.preview.streamInput || '—'
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {row.errors.length > 0 ? (
                              <ul className="space-y-1 text-xs text-rose-600">
                                {row.errors.map((error, idx) => (
                                  <li key={idx}>• {error}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                                Ready to update
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${statusStyle}`}>
                              <span>
                                {row.status === 'pending' && 'Pending'}
                                {row.status === 'uploading' && 'Updating'}
                                {row.status === 'success' && 'Updated'}
                                {row.status === 'error' && 'Failed'}
                                {row.status === 'blocked' && 'Blocked'}
                                {row.status === 'skipped' && 'Skipped'}
                              </span>
                            </div>
                            {row.message && (
                              <p className="mt-1 text-xs text-slate-600">{row.message}</p>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
